/**
 * URN ↔ URL codec (RFC §6) — ONE pure module; the ONLY place this knowledge
 * lives. Entity ids are percent-encoded path segments; a multi-segment URN id
 * (`urn:career:workshop:w1/werte`) encodes its id part as ONE URL segment with
 * `~` as the in-segment separator (`/workshops/w1~werte`).
 *
 * ── Encoding scheme (PINNED; round-trip is property-tested) ──
 * 1. Split the id on `/` into parts.
 * 2. `encodeURIComponent` each part, then additionally escape literal `~` as
 *    `%7E` (encodeURIComponent leaves `~` bare) — after this NO raw `~`
 *    remains inside any part.
 * 3. Join the parts with `~`.
 * Decoding is the exact inverse: split on `~` (every raw `~` IS a separator —
 * literals travel as `%7E`), `decodeURIComponent` each part, join with `/`.
 * No collision is possible: separator and escaped-tilde never overlap.
 *
 * Inner empty parts round-trip (`a//b` → `a~~b` → `a//b`).
 *
 * ── Rejected ids (throw) ──
 * - `""` — an empty id cannot be a path segment.
 * - `"."` and `".."` — WHATWG URL normalization collapses dot segments EVEN
 *   when percent-encoded (`/%2e%2e/` folds like `/../`; verified against
 *   Bun's URL), so these two ids cannot survive a URL round-trip in any
 *   encoding. Dots are fine anywhere else (`a.b`, `..a`, `./x` all work —
 *   only the whole-segment forms are hazardous, and a multi-part id always
 *   contains `~`).
 * `decodeUrnId("")` also throws (an empty segment addresses nothing).
 *
 * Route `:param` captures are decoded with `decodeUrnId` (the router, RFC §6):
 * entity routes get their original ids back, and consequently a raw `~` in any
 * param segment ALWAYS means the in-segment separator — that is the contract,
 * not an accident.
 */

/** Thrown for ids/segments the scheme cannot represent. */
export class UrnCodecError extends Error {
  constructor(message: string) {
    super(`[web-kit] URN codec: ${message}`);
    this.name = "UrnCodecError";
  }
}

/** Encode a URN id (possibly multi-segment, `/`-separated) as ONE URL path segment. */
export function encodeUrnId(id: string): string {
  if (id === "") throw new UrnCodecError("cannot encode an empty id.");
  if (id === "." || id === "..") {
    throw new UrnCodecError(
      `cannot encode id "${id}" — URL normalization collapses dot segments ` +
        `(even percent-encoded), so it cannot round-trip as a path segment.`
    );
  }
  return id
    .split("/")
    .map((part) => encodeURIComponent(part).replace(/~/g, "%7E"))
    .join("~");
}

/**
 * Decode ONE URL path segment back into the original URN id.
 * Throws `UrnCodecError` on empty or malformed (bad percent-encoding) segments.
 */
export function decodeUrnId(segment: string): string {
  if (segment === "") {
    throw new UrnCodecError("cannot decode an empty segment.");
  }
  try {
    return segment
      .split("~")
      .map((part) => decodeURIComponent(part))
      .join("/");
  } catch {
    throw new UrnCodecError(
      `malformed percent-encoding in segment "${segment}".`
    );
  }
}
